/**
 * File logger — mirrors console.log / warn / error / info to a persistent
 * file on the device so we can retrieve logs after a payment, crash, or
 * any other in-the-wild event without `adb logcat` access.
 *
 * Storage layout (Capacitor Filesystem, Directory.Documents):
 *   logs/app-YYYY-MM-DD.log         (current day's log)
 *   logs/app-YYYY-MM-DD-prev.log    (rolled when current exceeds MAX_BYTES)
 *
 * On Android these end up under:
 *   /storage/emulated/0/Android/data/<package>/files/Documents/logs/
 *   (accessible via `adb shell ls`, or via the Files app -> Internal storage)
 *
 * On iOS:
 *   <app sandbox>/Documents/logs/  (visible via Files app when
 *   UIFileSharingEnabled / LSSupportsOpeningDocumentsInPlace are enabled)
 *
 * On web (Capacitor.getPlatform() === 'web') the logger is a no-op for
 * file I/O; logs still appear in DevTools.
 */
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';

const LOG_DIR = 'logs';
const MAX_BYTES = 1 * 1024 * 1024; // 1 MB per file, rolled
const FLUSH_INTERVAL_MS = 1500;
const MAX_BUFFER_LINES = 500;

let initialized = false;
let isNative = false;
let buffer = [];
let flushTimer = null;
let currentFileName = null;
let approxBytesWritten = 0;

const pad2 = (n) => String(n).padStart(2, '0');
const todayStamp = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

const computeFileName = () => `${LOG_DIR}/app-${todayStamp()}.log`;
const prevFileName = (fname) => fname.replace(/\.log$/, '-prev.log');

const safeStringify = (value) => {
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';
  if (typeof value === 'string') return value;
  if (value instanceof Error) {
    return `${value.name}: ${value.message}\n${value.stack || ''}`;
  }
  try {
    return JSON.stringify(value);
  } catch (_) {
    try { return String(value); } catch (_e) { return '[unserializable]'; }
  }
};

const formatLine = (level, args) => {
  const ts = new Date().toISOString();
  const parts = Array.from(args).map(safeStringify);
  return `${ts} [${level}] ${parts.join(' ')}\n`;
};

const ensureDir = async () => {
  try {
    await Filesystem.mkdir({
      path: LOG_DIR,
      directory: Directory.Documents,
      recursive: true,
    });
  } catch (_) {
    // Directory already exists or non-fatal — ignore.
  }
};

const rollIfNeeded = async () => {
  if (approxBytesWritten < MAX_BYTES || !currentFileName) return;
  try {
    // Best-effort: delete previous roll, then rename current -> prev.
    try {
      await Filesystem.deleteFile({
        path: prevFileName(currentFileName),
        directory: Directory.Documents,
      });
    } catch (_) { /* may not exist */ }
    await Filesystem.rename({
      from: currentFileName,
      to: prevFileName(currentFileName),
      directory: Directory.Documents,
      toDirectory: Directory.Documents,
    });
    approxBytesWritten = 0;
  } catch (_) {
    // If rename fails, just truncate by overwriting with empty.
    try {
      await Filesystem.writeFile({
        path: currentFileName,
        data: '',
        directory: Directory.Documents,
        encoding: Encoding.UTF8,
      });
      approxBytesWritten = 0;
    } catch (_e) { /* give up */ }
  }
};

const flush = async () => {
  if (!isNative || buffer.length === 0) return;
  const lines = buffer;
  buffer = [];
  const fname = computeFileName();
  // Day rollover: changing file name → start fresh size tracking.
  if (fname !== currentFileName) {
    currentFileName = fname;
    approxBytesWritten = 0;
  }
  const blob = lines.join('');
  try {
    await Filesystem.appendFile({
      path: currentFileName,
      data: blob,
      directory: Directory.Documents,
      encoding: Encoding.UTF8,
    });
    approxBytesWritten += blob.length;
    await rollIfNeeded();
  } catch (_) {
    // appendFile fails if the file doesn't exist on some platforms; try
    // create-then-append once.
    try {
      await Filesystem.writeFile({
        path: currentFileName,
        data: blob,
        directory: Directory.Documents,
        encoding: Encoding.UTF8,
        recursive: true,
      });
      approxBytesWritten += blob.length;
    } catch (_e) {
      // Re-queue at the front so we don't lose lines, but cap the buffer.
      buffer = [...lines, ...buffer].slice(-MAX_BUFFER_LINES);
    }
  }
};

const scheduleFlush = () => {
  if (flushTimer) return;
  flushTimer = setTimeout(async () => {
    flushTimer = null;
    await flush();
  }, FLUSH_INTERVAL_MS);
};

const enqueue = (level, args) => {
  buffer.push(formatLine(level, args));
  if (buffer.length >= MAX_BUFFER_LINES) {
    // Drop oldest if we somehow overflow before a flush completes.
    buffer = buffer.slice(-MAX_BUFFER_LINES);
  }
  if (isNative) scheduleFlush();
};

const patchConsole = () => {
  const original = {
    log: console.log.bind(console),
    info: console.info ? console.info.bind(console) : console.log.bind(console),
    warn: console.warn.bind(console),
    error: console.error.bind(console),
    debug: console.debug ? console.debug.bind(console) : console.log.bind(console),
  };

  console.log = (...a) => { try { enqueue('LOG', a); } catch (_) {} original.log(...a); };
  console.info = (...a) => { try { enqueue('INFO', a); } catch (_) {} original.info(...a); };
  console.warn = (...a) => { try { enqueue('WARN', a); } catch (_) {} original.warn(...a); };
  console.error = (...a) => { try { enqueue('ERROR', a); } catch (_) {} original.error(...a); };
  console.debug = (...a) => { try { enqueue('DEBUG', a); } catch (_) {} original.debug(...a); };

  window.addEventListener('error', (e) => {
    try {
      enqueue('UNCAUGHT', [
        e.message,
        `at ${e.filename}:${e.lineno}:${e.colno}`,
        e.error?.stack || '',
      ]);
    } catch (_) { /* ignore */ }
  });
  window.addEventListener('unhandledrejection', (e) => {
    try {
      enqueue('UNHANDLED_REJECTION', [safeStringify(e.reason)]);
    } catch (_) { /* ignore */ }
  });
};

/**
 * Initialise the file logger. Safe to call multiple times.
 */
export const initFileLogger = async () => {
  if (initialized) return;
  initialized = true;

  const platform = Capacitor.getPlatform();
  isNative = platform === 'ios' || platform === 'android';

  patchConsole();

  if (!isNative) {
    console.log('[fileLogger] Web platform — console mirrored but not written to disk');
    return;
  }

  try {
    await ensureDir();
    currentFileName = computeFileName();
    // Probe current file size so rolling works after a relaunch.
    try {
      const stat = await Filesystem.stat({
        path: currentFileName,
        directory: Directory.Documents,
      });
      approxBytesWritten = stat?.size || 0;
    } catch (_) {
      approxBytesWritten = 0;
    }
    console.log(`[fileLogger] Initialised. Writing to Documents/${currentFileName} (size=${approxBytesWritten})`);
    // Flush whatever buffered before initFileLogger resolved.
    await flush();

    // Flush on visibility-hidden / pagehide so we don't lose the last lines
    // when the app is backgrounded or killed.
    const finalFlush = () => { flush().catch(() => {}); };
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') finalFlush();
    });
    window.addEventListener('pagehide', finalFlush);
    window.addEventListener('beforeunload', finalFlush);
  } catch (err) {
    isNative = false;
    // Restore via console.warn so the message itself gets buffered through
    // our patched logger; safe because patchConsole already ran.
    console.warn('[fileLogger] Init failed, falling back to console-only:', err?.message || err);
  }
};

/**
 * Read the current log file contents. Returns '' on web or on error.
 */
export const readCurrentLog = async () => {
  if (!isNative || !currentFileName) return '';
  await flush();
  try {
    const { data } = await Filesystem.readFile({
      path: currentFileName,
      directory: Directory.Documents,
      encoding: Encoding.UTF8,
    });
    return typeof data === 'string' ? data : '';
  } catch (_) {
    return '';
  }
};

/**
 * Returns the absolute on-device URI to the current log file (Android: file://...).
 */
export const getCurrentLogUri = async () => {
  if (!isNative || !currentFileName) return '';
  try {
    const { uri } = await Filesystem.getUri({
      path: currentFileName,
      directory: Directory.Documents,
    });
    return uri || '';
  } catch (_) {
    return '';
  }
};

/**
 * List every file under the logs/ folder along with size + uri.
 */
export const listLogFiles = async () => {
  if (!isNative) return [];
  try {
    const { files } = await Filesystem.readdir({
      path: LOG_DIR,
      directory: Directory.Documents,
    });
    const enriched = await Promise.all(
      (files || []).map(async (f) => {
        const path = `${LOG_DIR}/${f.name || f}`;
        let uri = '';
        try {
          const r = await Filesystem.getUri({ path, directory: Directory.Documents });
          uri = r?.uri || '';
        } catch (_) { /* ignore */ }
        return {
          name: f.name || f,
          size: f.size,
          mtime: f.mtime,
          uri,
        };
      })
    );
    return enriched;
  } catch (_) {
    return [];
  }
};
